import { z } from 'zod';

import {
  ESUTD_CARRIER_TYPE,
  ESUTD_TRANSPORTATION_TYPE,
  esutdBinSchema,
  esutdEntityIdSchema,
  esutdFuelSchema,
  esutdIinSchema,
  esutdIsoDateTimeSchema,
  esutdKatoCodeSchema,
  esutdLicensePlateSchema,
  esutdOdometerSchema,
  esutdSignStatusSchema,
  esutdSignatureXmlSchema,
  examResultSchema,
} from './esutd';

/**
 * Тела девяти методов пассажирского ЭПЛ — по одному zod-объекту на Java-DTO.
 * Имена полей совпадают с DTO ЕСУТД буква в букву: тело уходит в
 * `{ "method": …, "body": <этот объект> }` без дополнительной обёртки.
 */

const COMMENT_MAX = 1000;
const commentSchema = z.string().trim().min(1).max(COMMENT_MAX);
const optionalChallengeId = z.string().trim().min(1).optional();

// ─────────────── CREATE_WAYBILL_PASSENGER (IntegrationWaybillPassengerDTO) ───────────────

/**
 * `technicalInspectionPreTrip` при создании — плейсхолдер, но перезаписывается он НЕ
 * безусловно: значение затирает только сабмит стадии механика
 * (`WorkflowService.applyTechnicalInspectionValues`, prod-branch `WorkflowService.java:895`).
 * `TAKE_TECHNICAL_RESPONSIBILITY_WAYBILL_PASSENGER` стадию механика убирает
 * (`WaybillPassengerService.java:473-498` поле не трогает), и до тех пор — а в этом
 * сценарии навсегда — карточка ЕСУТД показывает именно то, что мы прислали
 * (`ui/.../passenger-form-view.component.html:101` рендерит сырое поле). Реальные вердикты
 * для печатной формы берутся из строк Workflow, а не отсюда (`WaybillDTO.java:64-67`).
 *
 * Механик и медработник обязательны всегда (`@NotBlank`), полей
 * `carrier*Responsibility` в партнёрском DTO нет — сократить цепочку нельзя.
 */
export const createWaybillPassengerSchema = z
  .object({
    carrierOrganizationBin: esutdBinSchema,
    medicalWorkerIin: esutdIinSchema,
    mechanicWorkerIin: esutdIinSchema,
    driversIin: z.array(esutdIinSchema).min(1, 'В ЭПЛ должен быть хотя бы один водитель'),
    startingKatoCode: esutdKatoCodeSchema,
    midKatoCodes: z.array(esutdKatoCodeSchema).optional(),
    finalKatoCode: esutdKatoCodeSchema,
    transportationType: z.literal(ESUTD_TRANSPORTATION_TYPE),
    carrierType: z.literal(ESUTD_CARRIER_TYPE),
    busLicensePlate: esutdLicensePlateSchema,
    technicalInspectionPreTrip: examResultSchema,
    planDepartureDateTime: esutdIsoDateTimeSchema,
    planArrivalDateTime: esutdIsoDateTimeSchema,
    factDepartureDateTime: esutdIsoDateTimeSchema.optional(),
    factArrivalDateTime: esutdIsoDateTimeSchema.optional(),
    endDateTime: esutdIsoDateTimeSchema.optional(),
    busStationName: z.string().trim().min(1).optional(),
    busStationKatoCode: esutdKatoCodeSchema.optional(),
    busStationAddress: z.string().trim().min(1).optional(),
  })
  // Дубли ЕСУТД отвергает сам («Список водителей содержит дубликаты ИИН»),
  // но ловить это до сетевого вызова дешевле.
  .refine(
    (body) => new Set(body.driversIin).size === body.driversIin.length,
    'Список водителей содержит дубликаты ИИН',
  );
export type CreateWaybillPassenger = z.infer<typeof createWaybillPassengerSchema>;

// ─────────────── SIGN_WAYBILL_PASSENGER (IntegrationSignDTO) ───────────────

/**
 * `xml` требуется только при `AGREE`: `checkSignature` вызывается в AGREE-ветке,
 * а `DISAGREE` подпись не проверяет вообще (дефект платформы — аннулировать
 * юридический документ можно одним API-ключом). Нашу защиту от случайного
 * аннулирования делает подтверждение оператора, а не эта схема.
 *
 * `organizationType` в DTO есть, но не читается нигде — не посылаем.
 */
export const signWaybillSchema = z
  .object({
    entityId: esutdEntityIdSchema,
    iin: esutdIinSchema,
    status: esutdSignStatusSchema,
    comment: commentSchema.optional(),
    xml: esutdSignatureXmlSchema.optional(),
    challengeId: optionalChallengeId,
  })
  .superRefine((body, ctx) => {
    if (body.status === 'AGREE' && !body.xml) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['xml'],
        message: 'Согласование стадии CARRIER требует ЭЦП ЮЛ перевозчика',
      });
    }
  });
export type SignWaybill = z.infer<typeof signWaybillSchema>;

// ─────────────── SIGN_..._MEDICAL_EXAMINATION (IntegrationMedicalExaminationDTO) ───────────────

export const medicalExaminationDetailSchema = z.object({
  driverIin: esutdIinSchema,
  preTripStatus: examResultSchema,
});

/**
 * `details.size()` обязан ТОЧНО равняться числу водителей ЭПЛ, иначе 400
 * `ERROR.DRIVERS_EMPTY`. Совпадение состава проверяет сборщик тела (часть D),
 * здесь — только непустота и уникальность.
 *
 * Медик подписывает и отказ (`preTripStatus=NO`), поэтому `xml` обязателен всегда.
 */
export const medicalExaminationSchema = z
  .object({
    entityId: esutdEntityIdSchema,
    iin: esutdIinSchema,
    details: z.array(medicalExaminationDetailSchema).min(1, 'Список решений медработника пуст'),
    xml: esutdSignatureXmlSchema,
    challengeId: optionalChallengeId,
  })
  .refine(
    (body) => new Set(body.details.map((d) => d.driverIin)).size === body.details.length,
    'Решения медработника содержат повтор ИИН водителя',
  );
export type MedicalExamination = z.infer<typeof medicalExaminationSchema>;

// ─────────────── SIGN_..._TECHNICAL_INSPECTION (IntegrationTechnicalInspectionDTO) ───────────────

const technicalInspectionCore = {
  entityId: esutdEntityIdSchema,
  /** В DTO без `@NotBlank`, но код требует: `ERROR.MECHANIC_REQUIRED`. */
  iin: esutdIinSchema,
  technicalInspectionPreTrip: examResultSchema,
  comment: commentSchema.optional(),
  challengeId: optionalChallengeId,
};

/** Отрицательный вердикт без комментария → 400 `TECHNICAL_INSPECTION_COMMENT_REQUIRED`. */
const requireCommentOnRejection = (
  body: { technicalInspectionPreTrip: string; comment?: string },
  ctx: z.RefinementCtx,
): void => {
  if (body.technicalInspectionPreTrip === 'NO' && !body.comment) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['comment'],
      message: 'При отрицательном результате техосмотра комментарий обязателен',
    });
  }
};

/**
 * Предрейсовый техосмотр: применяются только выездные показатели
 * (`applyTechnicalInspectionValues` остальные игнорирует), подпись обязательна
 * даже при отказе.
 */
export const technicalInspectionPreTripSchema = z
  .object({
    ...technicalInspectionCore,
    departureOdometer: esutdOdometerSchema.optional(),
    fuelDepartureBalance: esutdFuelSchema.optional(),
    fuelIssued: esutdFuelSchema.optional(),
    xml: esutdSignatureXmlSchema,
  })
  .superRefine(requireCommentOnRejection);
export type TechnicalInspectionPreTrip = z.infer<typeof technicalInspectionPreTripSchema>;

/**
 * Послерейсовый техосмотр: применяются только заездные показатели, ЭЦП НЕ требуется
 * (`checkSignature` вызывается только при `preTrip`). Именно этот шаг обязан закрыть
 * парность одометра и топлива — `validatePairedMandatoryFields`.
 */
export const technicalInspectionPostTripSchema = z
  .object({
    ...technicalInspectionCore,
    returnOdometer: esutdOdometerSchema.optional(),
    fuelReturnBalance: esutdFuelSchema.optional(),
  })
  .superRefine(requireCommentOnRejection);
export type TechnicalInspectionPostTrip = z.infer<typeof technicalInspectionPostTripSchema>;

export type TechnicalInspection = TechnicalInspectionPreTrip | TechnicalInspectionPostTrip;

// ─────────────── CONFIRM_READING / SET_RETURN_DATE_TIME / REVOKE / TAKE_TECH ───────────────

/** Подтверждение прочтения: ЭЦП не подписывается, полей `xml`/`challengeId` в DTO нет. */
export const confirmReadingSchema = z.object({
  entityId: esutdEntityIdSchema,
  iin: esutdIinSchema,
});
export type ConfirmReading = z.infer<typeof confirmReadingSchema>;

/** Отметка возврата закрывает стадию `ORGANIZATION`. Подписью её закрыть нельзя. */
export const returnDateTimeSchema = z.object({
  entityId: esutdEntityIdSchema,
  iin: esutdIinSchema,
  returnDateTime: esutdIsoDateTimeSchema,
  returnOdometer: esutdOdometerSchema.optional(),
  fuelReturnBalance: esutdFuelSchema.optional(),
});
export type ReturnDateTime = z.infer<typeof returnDateTimeSchema>;

/** `REVOKE_WAYBILL_PASSENGER`: `iin` в теле игнорируется, действие идёт от руководителя. */
export const entityActionSchema = z.object({
  entityId: esutdEntityIdSchema,
  iin: esutdIinSchema.optional(),
});
export type EntityAction = z.infer<typeof entityActionSchema>;

/** `TAKE_TECHNICAL_RESPONSIBILITY`: `iin` проверяется вручную, тип работника — `WORKER`. */
export const takeTechnicalResponsibilitySchema = z.object({
  entityId: esutdEntityIdSchema,
  iin: esutdIinSchema,
});
export type TakeTechnicalResponsibility = z.infer<typeof takeTechnicalResponsibilitySchema>;

// ─────────────── ISSUE_SIGN_ENVELOPE (канонический режим) ───────────────

/**
 * Заготовка на флип `ESUTD_SIGNING_CANONICAL_CUTOVER` / `STAGE_MAP_RATIFIED`.
 * Сегодня документы получают схему `legacy` и конверт не нужен; для legacy-документа
 * ЕСУТД отвечает `SIGN_ENVELOPE_NOT_APPLICABLE`.
 *
 * Заполнено должно быть РОВНО одно намерение — по нему выводится стадия.
 */
export const signEnvelopeRequestSchema = z
  .object({
    entityId: esutdEntityIdSchema,
    iin: esutdIinSchema,
    medicalDetails: z.array(medicalExaminationDetailSchema).min(1).optional(),
    technicalInspection: z
      .object({
        technicalInspectionPreTrip: examResultSchema,
        comment: commentSchema.optional(),
        departureOdometer: esutdOdometerSchema.optional(),
        returnOdometer: esutdOdometerSchema.optional(),
        fuelDepartureBalance: esutdFuelSchema.optional(),
        fuelIssued: esutdFuelSchema.optional(),
        fuelReturnBalance: esutdFuelSchema.optional(),
      })
      .optional(),
  })
  .refine(
    (body) => Number(Boolean(body.medicalDetails)) + Number(Boolean(body.technicalInspection)) <= 1,
    'В запросе конверта допустимо ровно одно намерение стадии',
  );
export type SignEnvelopeRequest = z.infer<typeof signEnvelopeRequestSchema>;

/** Ответ `IntegrationEnvelopeResponseDTO`. TTL конверта по умолчанию 600 секунд. */
export const signEnvelopeResponseSchema = z.object({
  challengeId: z.string().min(1),
  envelopeXml: z.string().min(1),
  tbsElementId: z.string().nullish(),
});
export type SignEnvelopeResponse = z.infer<typeof signEnvelopeResponseSchema>;

// ─────────────── чтение: /api/integration/workflows ───────────────

/**
 * `waybillPassengerProgress` и `documentStatus` — СТРОКИ, не enum: ЕСУТД может
 * добавить значение раньше нашего релиза (`EXPIRED` появляется без нашего участия),
 * и неизвестное значение не должно ронять поллинг. Смысл значений — в `esutd-stages.ts`.
 */
export const workflowStatusSchema = z.object({
  id: z.string().nullish(),
  entityId: z.string(),
  entityType: z.string().nullish(),
  documentNumber: z.number().nullish(),
  status: z.string().nullish(),
  waybillPassengerProgress: z.string().nullish(),
  documentStatus: z.string().nullish(),
  createdDateTime: z.string().nullish(),
  changedDate: z.string().nullish(),
});
export type EsutdWorkflowStatus = z.infer<typeof workflowStatusSchema>;

/** Spring `Page<WorkflowStatusDTO>`; неизвестные поля страницы отбрасываем. */
export const workflowsPageSchema = z.object({
  content: z.array(workflowStatusSchema).default([]),
  totalElements: z.number().nullish(),
  number: z.number().nullish(),
  size: z.number().nullish(),
  last: z.boolean().nullish(),
});
export type EsutdWorkflowsPage = z.infer<typeof workflowsPageSchema>;
